import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../../components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
    Plus,
    ClipboardCheck,
    Tags,
    PenLine,
    Link2,
    ArrowUpFromLine,
    Trash2,
    Copy,
    ExternalLink,
    Search,
    Filter,
    AlertCircle,
    Coins,
    Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { useSiteSettings } from "../../contexts/SiteSettingsContext";
import { usePanelT } from "../../hooks/usePanelT";
import TopUpDialog from "../../components/panel/TopUpDialog";

// ---------- Static catalog ---------------------------------------------------
const PLATFORM_TABS = [
    { id: "Meta", label: "Meta", subtitle: "Standard · Flex · Casino" },
    { id: "Google", label: "Google", subtitle: "Search · Display · YouTube" },
    { id: "TikTok", label: "TikTok", subtitle: "Spark · Pixel · Events API" },
    { id: "Snapchat", label: "Snapchat", subtitle: "AR Lens · Lookalikes" },
    { id: "Bing", label: "Microsoft", subtitle: "Search · Audience network" },
    { id: "Taboola", label: "Taboola", subtitle: "Native · Discovery" },
    { id: "Outbrain", label: "Outbrain", subtitle: "Native · Discovery" },
];

const VERTICALS = ["whiteHat", "grayHat", "casino"];

const INDUSTRY_TAGS = [
    "Cryptocurrency", "Online casino", "E-commerce", "Finance", "Mobile Apps",
    "Gambling", "Lead generation", "Adult", "Web3", "Arbitrage", "Nutra",
    "iGaming", "Dating", "Forex", "Dropshipping", "Affiliate marketing",
];

const TIMEZONES = [
    "UTC", "America/New_York", "America/Los_Angeles", "America/Anchorage",
    "America/Sao_Paulo", "Europe/London", "Europe/Berlin", "Europe/Istanbul",
    "Asia/Dubai", "Asia/Kolkata", "Asia/Singapore", "Asia/Tokyo",
    "Australia/Sydney",
];

const STATUS_BADGE = {
    pending: "bg-yellow-500/15 text-yellow-400 border-yellow-500/30",
    active: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
    suspended: "bg-red-500/15 text-red-400 border-red-500/30",
    replaced: "bg-blue-500/15 text-blue-400 border-blue-500/30",
    cancelled: "bg-white/[0.06] text-muted-foreground border-white/10",
    rejected: "bg-red-500/15 text-red-400 border-red-500/30",
};

// ---------- Wizard steps -----------------------------------------------------
const STEPS = [
    { id: "requirements", Icon: ClipboardCheck },
    { id: "industry", Icon: Tags },
    { id: "details", Icon: PenLine },
    { id: "content", Icon: Link2 },
    { id: "topup", Icon: ArrowUpFromLine },
];

function StepRail({ activeIdx, onJump, completed }) {
    const { t } = usePanelT();
    return (
        <div className="hidden md:flex w-72 shrink-0 flex-col gap-2 pr-6 border-r border-white/[0.06]">
            <div className="px-2 mb-3">
                <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-muted-foreground">
                    {t("wz.eyebrow")}
                </p>
                <p className="text-[12px] text-muted-foreground mt-1.5 leading-snug">
                    {t("wz.eyebrowSub")}
                </p>
            </div>
            {STEPS.map((s, i) => {
                const isActive = i === activeIdx;
                const isDone = completed.has(i);
                return (
                    <button
                        key={s.id}
                        onClick={() => isDone && onJump(i)}
                        data-testid={`wizard-rail-${s.id}`}
                        className={`flex items-start gap-3 text-left px-3 py-3 rounded-xl transition-colors ${
                            isActive ? "bg-white/[0.04]" : isDone ? "hover:bg-white/[0.03] cursor-pointer" : "opacity-40 cursor-not-allowed"
                        }`}
                    >
                        <div className={`w-9 h-9 rounded-lg grid place-items-center shrink-0 ${
                            isActive ? "bg-primary text-primary-foreground" :
                            isDone ? "bg-emerald-500/20 text-emerald-400" : "bg-white/[0.03] text-muted-foreground"
                        }`}>
                            <s.Icon className="w-4 h-4" />
                        </div>
                        <div className="min-w-0">
                            <p className="text-sm font-semibold leading-tight">{t(`wz.step.${s.id}`)}</p>
                            <p className="text-[11px] text-muted-foreground leading-snug mt-0.5">{t(`wz.step.${s.id}.d`)}</p>
                        </div>
                    </button>
                );
            })}
        </div>
    );
}

function CheckRequirements({ platform, accountType, minTopup, onContinue, onCancel }) {
    const { t } = usePanelT();
    const isMeta = platform === "Meta";
    return (
        <div className="space-y-6">
            <div>
                <h3 className="font-display font-semibold text-2xl">{t("wz.req.title", { p: platform })}</h3>
                <p className="text-muted-foreground text-sm mt-2 max-w-md">{t("wz.req.sub")}</p>
            </div>
            <div>
                <p className="text-sm font-semibold mb-3">{t("wz.req.need")}</p>
                <ul className="space-y-2.5 text-sm text-foreground/85">
                    {isMeta && (
                        <>
                            <li className="flex gap-2"><span className="text-primary">•</span> {t("wz.req.fbPage")}</li>
                            <li className="flex gap-2"><span className="text-primary">•</span> {t("wz.req.bmid")}</li>
                        </>
                    )}
                    <li className="flex gap-2"><span className="text-primary">•</span> {t("wz.req.links")}</li>
                    <li className="flex gap-2"><span className="text-primary">•</span> {t("wz.req.funds", { n: minTopup })}</li>
                </ul>
            </div>
            <div className="flex items-center gap-4 pt-2">
                <Button onClick={onContinue} data-testid="wizard-continue" className="bg-white text-background hover:bg-white/90 font-semibold px-6 h-10">
                    {t("continue")}
                </Button>
                <button onClick={onCancel} className="text-sm text-muted-foreground hover:text-foreground transition-colors">{t("cancel")}</button>
            </div>
        </div>
    );
}

function SpecifyIndustry({ form, setForm, onBack, onContinue }) {
    const { t } = usePanelT();
    const tags = form.industry_tags || [];
    const [draft, setDraft] = useState("");
    const toggleTag = (t) => {
        const has = tags.includes(t);
        setForm((f) => ({ ...f, industry_tags: has ? tags.filter((x) => x !== t) : [...tags, t] }));
    };
    const addCustom = (e) => {
        if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            const v = draft.trim();
            if (v && !tags.includes(v)) setForm((f) => ({ ...f, industry_tags: [...tags, v] }));
            setDraft("");
        }
    };
    const canContinue = tags.length >= 3;
    return (
        <div className="space-y-6">
            <div>
                <h3 className="font-display font-semibold text-2xl">{t("wz.ind.title")}</h3>
                <p className="text-muted-foreground text-sm mt-2">{t("wz.ind.sub")}</p>
            </div>
            <div>
                <p className="text-sm font-medium mb-3">{t("wz.ind.pick")}</p>
                <Input
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={addCustom}
                    placeholder={t("wz.ind.placeholder")}
                    data-testid="wizard-industry-input"
                    className="bg-background/60 border-white/10 h-11"
                />
                <p className="text-[11px] text-muted-foreground mt-1.5">{t("wz.ind.hint")}</p>
                {tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                        {tags.map((t) => (
                            <button key={t} onClick={() => toggleTag(t)} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-primary/15 border border-primary/30 text-[12px] text-primary">
                                {t} <span className="text-primary/70">×</span>
                            </button>
                        ))}
                    </div>
                )}
                <div className="flex flex-wrap gap-2 mt-4">
                    {INDUSTRY_TAGS.filter((t) => !tags.includes(t)).map((t) => (
                        <button key={t} onClick={() => toggleTag(t)} data-testid={`wizard-industry-${t.toLowerCase().replace(/\s/g, "-")}`} className="px-3 py-1.5 rounded-md bg-white/[0.03] border border-white/10 hover:border-white/30 text-[13px] transition-colors">
                            {t}
                        </button>
                    ))}
                </div>
            </div>
            <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={!!form.domains_change_often} onChange={(e) => setForm((f) => ({ ...f, domains_change_often: e.target.checked }))} data-testid="wizard-domains-change" className="w-4 h-4 accent-primary" />
                <span className="text-sm text-foreground/85">{t("wz.ind.domains")}</span>
            </label>
            <div className="flex items-center gap-4 pt-2">
                <button onClick={onBack} className="text-sm text-muted-foreground hover:text-foreground">{t("back")}</button>
                <Button onClick={onContinue} disabled={!canContinue} data-testid="wizard-continue" className="bg-white text-background hover:bg-white/90 font-semibold px-6 h-10">{t("continue")}</Button>
            </div>
        </div>
    );
}

function AddDetails({ form, setForm, platform, onBack, onContinue }) {
    const { t } = usePanelT();
    const isMeta = platform === "Meta";
    return (
        <div className="space-y-6">
            <div>
                <h3 className="font-display font-semibold text-2xl">{t("wz.det.title")}</h3>
                <p className="text-muted-foreground text-sm mt-2">
                    {t("wz.det.sub")}
                    {isMeta && t("wz.det.subMeta")}
                </p>
            </div>
            <div className="space-y-4">
                <p className="text-sm font-medium">{t("wz.det.accounts")}</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                        <Label>{t("wz.det.name")}</Label>
                        <Input value={form.account_name || ""} onChange={(e) => setForm((f) => ({ ...f, account_name: e.target.value }))} placeholder="MyBrand-1" data-testid="wizard-account-name" className="bg-background/60 border-white/10 h-11" />
                    </div>
                    <div className="space-y-1.5">
                        <Label>{t("wz.det.tz")}</Label>
                        <select value={form.timezone || "UTC"} onChange={(e) => setForm((f) => ({ ...f, timezone: e.target.value }))} data-testid="wizard-timezone" className="w-full bg-background/60 border border-white/10 rounded-lg px-3 h-11 text-sm focus:outline-none focus:border-primary/50">
                            {TIMEZONES.map((tz) => <option key={tz}>{tz}</option>)}
                        </select>
                    </div>
                </div>
                {isMeta && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                            <Label>{t("wz.det.type")}</Label>
                            <select value={form.account_type || "standard"} onChange={(e) => setForm((f) => ({ ...f, account_type: e.target.value }))} data-testid="wizard-account-type" className="w-full bg-background/60 border border-white/10 rounded-lg px-3 h-11 text-sm focus:outline-none focus:border-primary/50">
                                <option value="standard">{t("wz.det.std")}</option>
                                <option value="flex">{t("wz.det.flex")}</option>
                            </select>
                            <p className="text-[11px] text-muted-foreground mt-1" data-testid="wizard-account-type-help">
                                {form.account_type === "flex" ? t("wz.det.flexHelp") : t("wz.det.stdHelp")}
                            </p>
                        </div>
                        <div className="space-y-1.5">
                            <Label>{t("wz.det.bmid")}</Label>
                            <Input value={form.business_manager_id || ""} onChange={(e) => setForm((f) => ({ ...f, business_manager_id: e.target.value }))} placeholder="123456789" data-testid="wizard-bmid" className="bg-background/60 border-white/10 h-11" />
                        </div>
                    </div>
                )}
            </div>
            <div className="flex items-center gap-4 pt-2">
                <button onClick={onBack} className="text-sm text-muted-foreground hover:text-foreground">{t("back")}</button>
                <Button onClick={onContinue} disabled={!form.account_name} data-testid="wizard-continue" className="bg-white text-background hover:bg-white/90 font-semibold px-6 h-10">{t("continue")}</Button>
            </div>
        </div>
    );
}

function AddContent({ form, setForm, platform, onBack, onContinue }) {
    const { t } = usePanelT();
    const isMeta = platform === "Meta";
    const settings = useSiteSettings().settings;
    const links = form.advertising_links || [""];
    const pages = form.facebook_pages || [{ url: "", name: "" }];
    const setLink = (i, v) => { const c = [...links]; c[i] = v; setForm((f) => ({ ...f, advertising_links: c })); };
    const addLink = () => setForm((f) => ({ ...f, advertising_links: [...links, ""] }));
    const removeLink = (i) => setForm((f) => ({ ...f, advertising_links: links.filter((_, j) => j !== i) }));
    const setPage = (i, key, v) => { const c = [...pages]; c[i] = { ...c[i], [key]: v }; setForm((f) => ({ ...f, facebook_pages: c })); };
    const addPage = () => setForm((f) => ({ ...f, facebook_pages: [...pages, { url: "", name: "" }] }));
    const removePage = (i) => setForm((f) => ({ ...f, facebook_pages: pages.filter((_, j) => j !== i) }));

    const inviteUrl = settings.meta_page_invite_url || "https://www.facebook.com/AdriseAgency";
    const inviteEmail = (settings.meta_page_invite_email || "").trim();
    const copy = (txt) => { navigator.clipboard?.writeText(txt).then(() => toast.success(t("copied"))).catch(() => toast.error(t("clipboardBlocked"))); };
    const linksValid = links.length > 0 && links.every((l) => l.trim().length > 0);
    const pagesValid = !isMeta || (pages.length > 0 && pages.every((p) => p.url && p.name));
    const inviteValid = !isMeta || form.page_invite_confirmed;
    const canContinue = linksValid && pagesValid && inviteValid;

    return (
        <div className="space-y-6">
            <div>
                <h3 className="font-display font-semibold text-2xl">{t("wz.ct.links", { n: links.length })}</h3>
                <p className="text-muted-foreground text-sm mt-2 max-w-lg leading-snug">{t("wz.ct.linksSub")}</p>
            </div>
            <div className="space-y-2">
                {links.map((l, i) => (
                    <div key={i} className="flex items-center gap-2">
                        <Input value={l} onChange={(e) => setLink(i, e.target.value)} placeholder="https://yourbrand.com/" data-testid={`wizard-link-${i}`} className="bg-background/60 border-white/10 h-11" />
                        {links.length > 1 && (
                            <button onClick={() => removeLink(i)} className="text-muted-foreground hover:text-primary" type="button"><Trash2 className="w-4 h-4" /></button>
                        )}
                    </div>
                ))}
                <button onClick={addLink} type="button" data-testid="wizard-add-link" className="text-sm text-primary hover:text-primary/80 inline-flex items-center gap-1">
                    <Plus className="w-3.5 h-3.5" /> {t("wz.ct.addLink")}
                </button>
            </div>

            {isMeta && (
                <>
                    <div>
                        <h3 className="font-display font-semibold text-xl mt-4">{t("wz.ct.pages", { n: pages.length })}</h3>
                        <p className="text-muted-foreground text-sm mt-2 max-w-lg leading-snug">{t("wz.ct.pagesSub")}</p>
                    </div>
                    <div className="space-y-2">
                        {pages.map((p, i) => (
                            <div key={i} className="grid grid-cols-1 md:grid-cols-[1fr_240px_auto] gap-2 items-center">
                                <Input value={p.url} onChange={(e) => setPage(i, "url", e.target.value)} placeholder="https://www.facebook.com/yourpage" data-testid={`wizard-fb-url-${i}`} className="bg-background/60 border-white/10 h-11" />
                                <Input value={p.name} onChange={(e) => setPage(i, "name", e.target.value)} placeholder={t("wz.ct.pageName")} data-testid={`wizard-fb-name-${i}`} className="bg-background/60 border-white/10 h-11" />
                                {pages.length > 1 && (
                                    <button onClick={() => removePage(i)} className="text-muted-foreground hover:text-primary" type="button"><Trash2 className="w-4 h-4" /></button>
                                )}
                            </div>
                        ))}
                        <button onClick={addPage} type="button" data-testid="wizard-add-fb" className="text-sm text-primary hover:text-primary/80 inline-flex items-center gap-1">
                            <Plus className="w-3.5 h-3.5" /> {t("wz.ct.addPage")}
                        </button>
                    </div>

                    <div className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-4">
                        <p className="text-sm font-semibold mb-2">{t("wz.ct.invites")}</p>
                        <p className="text-[13px] text-muted-foreground leading-snug mb-3">
                            {t("wz.ct.invitesText")}{" "}
                            <a href="https://www.facebook.com/help/187316341316631" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary/80">{t("wz.ct.howInvite")}</a>
                        </p>
                        <div className="flex items-center gap-2 mb-3">
                            <code className="flex-1 px-3 py-2.5 rounded-lg bg-background/60 border border-white/10 text-[12px] font-mono text-primary truncate">{inviteUrl}</code>
                            <button onClick={() => copy(inviteUrl)} className="px-2.5 py-2.5 rounded-lg border border-white/10 bg-white/[0.02] hover:bg-white/[0.06]" type="button" data-testid="wizard-copy-invite"><Copy className="w-3.5 h-3.5" /></button>
                            <a href={inviteUrl} target="_blank" rel="noopener noreferrer" className="px-2.5 py-2.5 rounded-lg border border-white/10 bg-white/[0.02] hover:bg-white/[0.06] inline-flex items-center"><ExternalLink className="w-3.5 h-3.5" /></a>
                        </div>
                        {inviteEmail && (
                            <div className="mb-3 pt-3 border-t border-white/[0.06]" data-testid="wizard-invite-email-block">
                                <p className="text-sm font-semibold mb-1">{t("wz.ct.orEmail")}</p>
                                <p className="text-[13px] text-muted-foreground leading-snug mb-2">{t("wz.ct.emailText")}</p>
                                <div className="flex items-center gap-2">
                                    <code className="flex-1 px-3 py-2.5 rounded-lg bg-background/60 border border-white/10 text-[12px] font-mono text-primary truncate" data-testid="wizard-invite-email">{inviteEmail}</code>
                                    <button onClick={() => copy(inviteEmail)} className="px-2.5 py-2.5 rounded-lg border border-white/10 bg-white/[0.02] hover:bg-white/[0.06]" type="button" data-testid="wizard-copy-invite-email"><Copy className="w-3.5 h-3.5" /></button>
                                </div>
                            </div>
                        )}
                        <label className="flex items-start gap-3 cursor-pointer">
                            <input type="checkbox" checked={!!form.page_invite_confirmed} onChange={(e) => setForm((f) => ({ ...f, page_invite_confirmed: e.target.checked }))} data-testid="wizard-invite-confirm" className="w-4 h-4 accent-primary mt-1" />
                            <span className="text-[13px] text-foreground/85 leading-snug">{t("wz.ct.confirm")}</span>
                        </label>
                    </div>
                </>
            )}

            <div className="flex items-center gap-4 pt-2">
                <button onClick={onBack} className="text-sm text-muted-foreground hover:text-foreground">{t("back")}</button>
                <Button onClick={onContinue} disabled={!canContinue} data-testid="wizard-continue" className="bg-white text-background hover:bg-white/90 font-semibold px-6 h-10">{t("continue")}</Button>
            </div>
        </div>
    );
}

function TopUp({ form, setForm, platform, onBack, onSubmit, submitting, walletBalance, feePercent, minTopup }) {
    const { t } = usePanelT();
    const amount = Number(form.initial_topup_usd) || 0;
    const fee = Math.round(amount * (feePercent / 100) * 100) / 100;
    const net = Math.round((amount - fee) * 100) / 100;
    const accountName = form.account_name || t("account");
    const insufficient = amount > walletBalance;
    const valid = amount >= minTopup && !insufficient;
    return (
        <div className="space-y-6">
            <div>
                <h3 className="font-display font-semibold text-2xl">{t("wz.tp.title")}</h3>
                <p className="text-muted-foreground text-sm mt-2">{t("wz.tp.sub")}</p>
            </div>

            <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-4 flex items-center justify-between">
                <div>
                    <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">{t("walletBalance")}</p>
                    <p className="font-display font-bold text-2xl mt-0.5" data-testid="wizard-wallet-balance">${walletBalance.toFixed(2)}</p>
                </div>
                {insufficient && (
                    <div className="text-right">
                        <p className="text-[10px] font-mono uppercase tracking-wider text-red-400">{t("insufficient")}</p>
                        <p className="text-[11px] text-muted-foreground">{t("topUpWalletFirst")}</p>
                    </div>
                )}
            </div>

            <div>
                <p className="text-sm font-medium mb-1">{t("wz.tp.amount")}</p>
                <p className="text-[12px] text-muted-foreground mb-3">{t("wz.tp.min", { n: minTopup })}</p>
                <div className="grid grid-cols-1 md:grid-cols-[1fr_240px] gap-3 items-center">
                    <p className="text-sm">{accountName}</p>
                    <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                        <Input type="number" min={minTopup} value={form.initial_topup_usd ?? ""} onChange={(e) => setForm((f) => ({ ...f, initial_topup_usd: e.target.value === "" ? "" : Number(e.target.value) }))} placeholder={`min ${minTopup}`} data-testid="wizard-topup-amount" className="bg-background/60 border-white/10 h-11 pl-7" />
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-3 gap-2" data-testid="wizard-fee-breakdown">
                <div className="rounded-xl border border-white/10 bg-background/50 px-3 py-2.5">
                    <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{t("youPay")}</p>
                    <p className="font-display font-bold text-lg tabular-nums" data-testid="wizard-fee-pay">${amount.toFixed(2)}</p>
                </div>
                <div className="rounded-xl border border-white/10 bg-background/50 px-3 py-2.5">
                    <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{t("fee")} {feePercent.toFixed(feePercent % 1 ? 2 : 0)}%</p>
                    <p className="font-display font-bold text-lg tabular-nums text-red-300" data-testid="wizard-fee-amount">− ${fee.toFixed(2)}</p>
                </div>
                <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/[0.06] px-3 py-2.5">
                    <p className="text-[10px] font-mono uppercase tracking-wider text-emerald-400">{t("toAdAccount")}</p>
                    <p className="font-display font-bold text-lg tabular-nums text-emerald-300" data-testid="wizard-fee-net">${net.toFixed(2)}</p>
                </div>
            </div>
            <p className="text-[12px] text-muted-foreground" data-testid="wizard-fee-summary">
                {t("wz.tp.summary", { a: amount.toFixed(2), fee: feePercent.toFixed(2), f: fee.toFixed(2), n: net.toFixed(2), name: accountName })}
            </p>
            <div className="flex items-center gap-4 pt-2">
                <button onClick={onBack} className="text-sm text-muted-foreground hover:text-foreground">{t("back")}</button>
                <Button onClick={onSubmit} disabled={!valid || submitting} data-testid="wizard-submit" className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-6 h-10">
                    {submitting ? t("wz.submitting") : insufficient ? t("topUpWalletFirst") : t("wz.submit")}
                </Button>
            </div>
        </div>
    );
}

function RequestWizard({ open, onClose, onCreated, defaultPlatform = "Meta", walletBalance = 0 }) {
    const { t } = usePanelT();
    const [stepIdx, setStepIdx] = useState(0);
    const [completed, setCompleted] = useState(new Set());
    const [platform, setPlatform] = useState(defaultPlatform);
    const [vertical, setVertical] = useState("whiteHat");
    const [submitting, setSubmitting] = useState(false);
    const [policy, setPolicy] = useState({ fee_percent: 3, min_topups: { meta_standard: 250, meta_flex: 500, google: 250, tiktok: 250, snapchat: 250, bing: 250, taboola: 250, outbrain: 250 } });
    const [form, setForm] = useState({
        account_type: "standard", account_name: "", timezone: "UTC", business_manager_id: "",
        industry_tags: [], domains_change_often: false, advertising_links: [""],
        facebook_pages: [{ url: "", name: "" }], page_invite_confirmed: false,
        initial_topup_usd: 250, note: "",
    });

    useEffect(() => {
        if (!open) { setStepIdx(0); setCompleted(new Set()); }
        else setPlatform(defaultPlatform);
    }, [open, defaultPlatform]);

    useEffect(() => {
        if (!open) return;
        api.get("/me/ad-policy")
            .then(({ data }) => {
                if (data?.min_topups && data?.fee_percent !== undefined) setPolicy(data);
            })
            .catch(() => {});
    }, [open]);

    const getMinTopup = (plat = platform, accType = form.account_type) => {
        const p = (plat || "").toLowerCase();
        if (p === "meta") return accType === "flex" ? Number(policy.min_topups?.meta_flex || 500) : Number(policy.min_topups?.meta_standard || 250);
        if (p === "google") return Number(policy.min_topups?.google || 250);
        if (p === "tiktok") return Number(policy.min_topups?.tiktok || 250);
        if (p === "snapchat") return Number(policy.min_topups?.snapchat || 250);
        if (p === "bing") return Number(policy.min_topups?.bing || 250);
        if (p === "taboola") return Number(policy.min_topups?.taboola || 250);
        if (p === "outbrain") return Number(policy.min_topups?.outbrain || 250);
        return 250;
    };

    useEffect(() => {
        const min = getMinTopup(platform, form.account_type);
        if ((form.initial_topup_usd || 0) < min) setForm((f) => ({ ...f, initial_topup_usd: min }));
    }, [form.account_type, platform, policy]);

    const next = () => { setCompleted((c) => new Set(c).add(stepIdx)); setStepIdx((i) => Math.min(i + 1, STEPS.length - 1)); };
    const back = () => setStepIdx((i) => Math.max(i - 1, 0));

    const submit = async () => {
        setSubmitting(true);
        try {
            const payload = { platform, vertical, ...form,
                advertising_links: (form.advertising_links || []).filter((x) => x.trim()),
                facebook_pages: (form.facebook_pages || []).filter((p) => p.url),
            };
            await api.post("/me/ad-accounts/request", payload);
            toast.success(t("acc.submitted"));
            onCreated?.();
            onClose();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || t("failed"));
        } finally { setSubmitting(false); }
    };

    const stepBody = () => {
        switch (STEPS[stepIdx].id) {
            case "requirements":
                return (
                    <div className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label>{t("wz.platform")}</Label>
                                <select value={platform} onChange={(e) => setPlatform(e.target.value)} data-testid="wizard-platform" className="w-full bg-background/60 border border-white/10 rounded-lg px-3 h-11 text-sm focus:outline-none focus:border-primary/50">
                                    {PLATFORM_TABS.map((p) => <option key={p.id}>{p.id}</option>)}
                                </select>
                            </div>
                            <div className="space-y-1.5">
                                <Label>{t("wz.vertical")}</Label>
                                <select value={vertical} onChange={(e) => setVertical(e.target.value)} data-testid="wizard-vertical" className="w-full bg-background/60 border border-white/10 rounded-lg px-3 h-11 text-sm focus:outline-none focus:border-primary/50">
                                    {VERTICALS.map((v) => <option key={v} value={v}>{t(`v.${v}`)}</option>)}
                                </select>
                            </div>
                        </div>
                        <CheckRequirements platform={platform} accountType={form.account_type} minTopup={getMinTopup(platform, form.account_type)} onContinue={next} onCancel={onClose} />
                    </div>
                );
            case "industry":
                return <SpecifyIndustry form={form} setForm={setForm} onBack={back} onContinue={next} />;
            case "details":
                return <AddDetails form={form} setForm={setForm} platform={platform} onBack={back} onContinue={next} />;
            case "content":
                return <AddContent form={form} setForm={setForm} platform={platform} onBack={back} onContinue={next} />;
            case "topup":
                return <TopUp form={form} setForm={setForm} platform={platform} feePercent={Number(policy.fee_by_vertical?.[vertical] ?? policy.fee_percent ?? 4)} minTopup={getMinTopup(platform, form.account_type)} onBack={back} onSubmit={submit} submitting={submitting} walletBalance={walletBalance} />;
            default: return null;
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent data-testid="request-wizard" className="bg-card border-white/[0.08] max-w-3xl p-0 overflow-hidden gap-0">
                <DialogHeader className="px-6 pt-6">
                    <DialogTitle className="sr-only">{t("wz.dialogTitle")}</DialogTitle>
                    <DialogDescription className="sr-only">{t("wz.eyebrowSub")}</DialogDescription>
                </DialogHeader>
                <div className="px-6 pt-2 pb-0">
                    <p data-testid="wizard-policy-info" className="text-[11px] text-muted-foreground">
                        {t("wz.policy", { fee: Number(policy.fee_by_vertical?.[vertical] ?? policy.fee_percent ?? 4).toFixed(2), v: vertical === "casino" ? t("v.casinoShort") : t(`v.${vertical}`), tier: policy.tier || "Starter" })}
                    </p>
                </div>
                <div className="flex p-6 max-h-[80vh] overflow-y-auto">
                    <StepRail activeIdx={stepIdx} completed={completed} onJump={(i) => setStepIdx(i)} />
                    <div className="flex-1 md:pl-6 min-w-0">{stepBody()}</div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

// ---------- Page ------------------------------------------------------------
function PlatformSidebar({ accounts, activeTab, setActiveTab }) {
    const { t } = usePanelT();
    const items = PLATFORM_TABS.map((tab) => ({
        tab,
        count: accounts.filter((a) => a.platform === tab.id.toLowerCase()).length,
    }));
    return (
        <aside className="w-full lg:w-64 shrink-0 lg:sticky lg:top-24 self-start">
            {/* Mobile: horizontal chips */}
            <div className="lg:hidden -mx-4 px-4 overflow-x-auto no-scrollbar" data-testid="platform-chips">
                <div className="flex gap-2 w-max pb-1">
                    {items.map(({ tab, count }) => {
                        const active = activeTab === tab.id;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                data-testid={`platform-chip-${tab.id}`}
                                className={`h-10 px-4 rounded-full border text-sm font-medium inline-flex items-center gap-2 whitespace-nowrap transition-colors ${
                                    active
                                        ? "bg-primary/15 border-primary/40 text-primary"
                                        : "bg-card border-white/10 text-foreground/80"
                                }`}
                            >
                                {tab.label}
                                {count > 0 && (
                                    <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-md bg-white/[0.08]">{count}</span>
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>
            {/* Desktop: vertical list */}
            <div className="hidden lg:block bg-card border border-white/[0.07] rounded-2xl p-3">
                <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-muted-foreground px-3 py-2">
                    {t("acc.platforms")}
                </p>
                <ul className="space-y-0.5">
                    {items.map(({ tab, count }) => {
                        const active = activeTab === tab.id;
                        return (
                            <li key={tab.id}>
                                <button
                                    onClick={() => setActiveTab(tab.id)}
                                    data-testid={`platform-tab-${tab.id}`}
                                    className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-left transition-colors ${
                                        active ? "bg-white/[0.06]" : "hover:bg-white/[0.03]"
                                    }`}
                                >
                                    <div className="min-w-0">
                                        <p className={`text-sm font-medium truncate ${active ? "text-foreground" : "text-foreground/85"}`}>
                                            {tab.label}
                                        </p>
                                        <p className="text-[11px] text-muted-foreground truncate">{tab.subtitle}</p>
                                    </div>
                                    <div className="flex items-center gap-1.5 shrink-0 ml-2">
                                        {count > 0 && (
                                            <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-md bg-white/[0.06]">
                                                {count}
                                            </span>
                                        )}
                                    </div>
                                </button>
                            </li>
                        );
                    })}
                </ul>
            </div>
        </aside>
    );
}

function AccountRow({ account, onCancel, onTopUp }) {
    const { t } = usePanelT();
    return (
        <div
            data-testid={`ad-account-${account.id}`}
            className="px-4 md:px-5 py-4 grid grid-cols-[1fr_auto] md:grid-cols-[1fr_auto_auto_auto_auto] items-center gap-3 md:gap-4 hover:bg-white/[0.02] transition-colors"
        >
            <div className="min-w-0">
                <p className="font-medium text-sm truncate">
                    {account.account_name || account.name}
                </p>
                <div className="flex items-center gap-1.5 mt-0.5">
                    <code className="text-[11px] font-mono text-muted-foreground truncate">
                        {account.external_id || t("acc.idPending")}
                    </code>
                    {account.account_type && (
                        <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                            · {account.account_type}
                        </span>
                    )}
                    {account.timezone && (
                        <span className="text-[10px] font-mono text-muted-foreground">
                            · {account.timezone}
                        </span>
                    )}
                </div>
            </div>
            <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full border ${STATUS_BADGE[account.status] || ""}`}>
                {t(`st.${account.status}`)}
            </span>
            <div className="text-right">
                <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{t("balance")}</p>
                <p className="font-display font-semibold text-sm">${(account.balance || 0).toLocaleString()}</p>
            </div>
            <div className="text-right hidden md:block">
                <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{t("topUps")}</p>
                <p className="font-display font-semibold text-sm">${(account.initial_topup_usd || 0).toLocaleString()}</p>
            </div>
            <div className="hidden md:flex items-center gap-1.5">
                {account.cancellable ? (
                    <Button size="sm" variant="outline" onClick={() => onCancel(account)} data-testid={`ad-account-cancel-${account.id}`} className="h-8 border-red-500/30 text-red-300 bg-red-500/[0.05] hover:bg-red-500/[0.12] text-xs">
                        {t("acc.cancelRefund")}
                    </Button>
                ) : account.status === "active" ? (
                    <Button size="sm" variant="outline" onClick={() => onTopUp(account)} data-testid={`ad-account-topup-${account.id}`} className="h-8 border-primary/30 text-primary bg-primary/[0.05] hover:bg-primary/[0.12] text-xs">
                        <Plus className="w-3 h-3 mr-1" /> {t("acc.topUp")}
                    </Button>
                ) : null}
            </div>
        </div>
    );
}


export default function PanelAdAccounts() {
    const { t } = usePanelT();
    const [accounts, setAccounts] = useState([]);
    const [topUpFor, setTopUpFor] = useState(null);
    const [walletBalance, setWalletBalance] = useState(0);
    const [activeTab, setActiveTab] = useState("Meta");
    const [search, setSearch] = useState("");
    const [filter, setFilter] = useState("all"); // all | active | disabled
    const [open, setOpen] = useState(false);

    const load = async () => {
        try {
            const [a, w] = await Promise.all([
                api.get("/me/ad-accounts"),
                api.get("/me/wallet"),
            ]);
            setAccounts(a.data);
            setWalletBalance(Number(w.data?.usd_balance ?? 0));
        } catch { /* ignore */ }
    };
    useEffect(() => { load(); }, []);

    const cancelRequest = async (acc) => {
        if (!window.confirm(t("acc.confirmCancel", { n: Number(acc.wallet_debit_applied || 0).toFixed(2) }))) return;
        try {
            await api.post(`/me/ad-accounts/${acc.id}/cancel`);
            toast.success(t("acc.cancelled"));
            load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || t("acc.cancelFail"));
        }
    };

    const visibleAccounts = useMemo(() => {
        const platformLower = activeTab.toLowerCase();
        return accounts
            .filter((a) => a.platform === platformLower)
            .filter((a) => {
                if (filter === "active") return a.status === "active";
                if (filter === "disabled") return a.status === "suspended";
                return true;
            })
            .filter((a) => {
                if (!search.trim()) return true;
                const t = search.toLowerCase();
                return (
                    (a.account_name || "").toLowerCase().includes(t) ||
                    (a.external_id || "").toLowerCase().includes(t) ||
                    (a.name || "").toLowerCase().includes(t)
                );
            });
    }, [accounts, activeTab, filter, search]);

    return (
        <div data-testid="panel-ad-accounts" className="space-y-6">
            <div>
                <h1 className="font-display font-bold text-2xl md:text-4xl tracking-tight">
                    {t("acc.title")}
                </h1>
                <p className="text-muted-foreground mt-1 text-sm hidden sm:block">
                    {t("acc.sub")}
                </p>
            </div>

            <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
                <PlatformSidebar accounts={accounts} activeTab={activeTab} setActiveTab={setActiveTab} />

                <div className="flex-1 min-w-0">
                    {/* Tab header */}
                    <div className="bg-card border border-white/[0.07] rounded-2xl mb-4">
                        <div className="px-5 py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3 border-b border-white/[0.06]">
                            <div>
                                <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-muted-foreground">
                                    {activeTab}
                                </p>
                                <h2 className="font-display font-bold text-xl">{t("acc.agencyAccounts")}</h2>
                            </div>
                            <Button
                                onClick={() => setOpen(true)}
                                data-testid="request-account-button"
                                className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold h-11 md:h-10 w-full md:w-auto"
                            >
                                <Plus className="w-4 h-4 mr-2" /> {t("acc.request")}
                            </Button>
                        </div>

                                <div className="px-5 py-3 flex flex-col md:flex-row md:items-center gap-3 border-b border-white/[0.06]">
                                    <div className="relative flex-1 max-w-md">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                                        <Input
                                            value={search}
                                            onChange={(e) => setSearch(e.target.value)}
                                            placeholder={t("acc.search")}
                                            data-testid="ad-account-search"
                                            className="bg-background/60 border-white/10 h-9 pl-8"
                                        />
                                    </div>
                                    <div className="flex gap-1 bg-background/60 border border-white/10 rounded-lg p-0.5">
                                        {["all", "active", "disabled"].map((f) => (
                                            <button
                                                key={f}
                                                onClick={() => setFilter(f)}
                                                data-testid={`ad-account-filter-${f}`}
                                                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                                                    filter === f ? "bg-white/10 text-foreground" : "text-muted-foreground hover:text-foreground"
                                                }`}
                                            >
                                                {t(`acc.filter.${f}`)}
                                            </button>
                                        ))}
                                    </div>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        className="hidden md:inline-flex h-9 border-white/10 bg-white/[0.02] hover:bg-white/[0.06] text-xs"
                                    >
                                        <Filter className="w-3 h-3 mr-1.5" /> {t("acc.filters")}
                                    </Button>
                                </div>

                                {visibleAccounts.length === 0 ? (
                                    <div className="px-5 py-12 text-center text-muted-foreground text-sm">
                                        <AlertCircle className="w-8 h-8 mx-auto mb-3 opacity-50" />
                                        {t("acc.empty", { p: activeTab })}
                                        <button
                                            onClick={() => setOpen(true)}
                                            className="text-primary hover:text-primary/80 ml-1"
                                        >
                                            {t("acc.emptyCta")}
                                        </button>
                                    </div>
                                ) : (
                                    <div className="divide-y divide-white/[0.04]">
                                        <div className="hidden md:grid px-5 py-2.5 grid-cols-[1fr_auto_auto_auto_auto] gap-4 text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">
                                            <span>{t("account")}</span>
                                            <span className="text-right">{t("status")}</span>
                                            <span className="text-right">{t("balance")}</span>
                                            <span className="text-right">{t("topUps")}</span>
                                            <span className="text-right">{t("action")}</span>
                                        </div>
                                        {visibleAccounts.map((a) => <AccountRow key={a.id} account={a} onCancel={cancelRequest} onTopUp={setTopUpFor} />)}
                                    </div>
                                )}
                    </div>
                </div>
            </div>

            <RequestWizard
                open={open}
                onClose={() => setOpen(false)}
                onCreated={load}
                defaultPlatform={activeTab}
                walletBalance={walletBalance}
            />
            <TopUpDialog account={topUpFor} open={Boolean(topUpFor)} onOpenChange={(o) => !o && setTopUpFor(null)} walletBalance={walletBalance} onDone={load} />
        </div>
    );
}
